import { Box, Paper, Stack, Typography } from '@mui/material';
import { ChartsTooltipContainer, useItemTooltip } from '@mui/x-charts';
import { useTranslation } from 'react-i18next';
import { colors } from '../../core/theme/theme';
import { formatDuration, formatHoursDecimal } from './reportFormat';
import type { DayDetail } from './reportDailyDetails';
import { nonWorkingDay } from './workingDays';

const MAX_ROWS = 8;

type Props = { details: DayDetail[]; dayLabels: string[] };

/** Inner content — reads the hovered bar and renders that day's breakdown. */
export function DayBreakdownContent({ details, dayLabels }: Props) {
  const { t } = useTranslation();
  const tooltip = useItemTooltip<'bar'>();
  const dataIndex = tooltip?.identifier?.dataIndex;
  if (dataIndex == null) return null;

  const day = details[dataIndex];
  if (!day || day.totalSeconds === 0) return null;

  const shown = day.entries.slice(0, MAX_ROWS);
  const remaining = day.entries.length - shown.length;

  return (
    <Paper
      elevation={3}
      sx={{ borderRadius: 0, border: `1px solid ${colors.lightGray}`, p: 1.5, minWidth: 220, maxWidth: 340 }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 1, gap: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {dayLabels[dataIndex] ?? ''}
          </Typography>
          {(() => {
            // Hours logged on a weekend or a public holiday deserve a label:
            // the bar alone reads as an ordinary day.
            const reason = nonWorkingDay(day.date);
            if (!reason) return null;
            return (
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {reason.kind === 'holiday'
                  ? t('kronos.report.nonWorkingHoliday', { name: reason.name })
                  : t('kronos.report.nonWorkingWeekend')}
              </Typography>
            );
          })()}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.bodyText, whiteSpace: 'nowrap' }}>
          {formatHoursDecimal(day.totalSeconds)}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        {shown.map((e, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: colors.darkBg, mr: 0.5 }}>
                {e.projectCode}
              </Typography>
              <Typography component="span" variant="caption" sx={{ color: colors.textMuted }}>
                {e.designationName} · {e.userName}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
              {formatDuration(e.durationSeconds)}
            </Typography>
          </Stack>
        ))}
        {remaining > 0 && (
          <Typography variant="caption" sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
            {t('kronos.report.dayTooltip.moreEntries', { count: remaining })}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

/** Chart `slots.tooltip` component — forces item-trigger and positions near the cursor. */
export default function DayBreakdownTooltip({ details, dayLabels }: Props) {
  return (
    <ChartsTooltipContainer trigger="item">
      <DayBreakdownContent details={details} dayLabels={dayLabels} />
    </ChartsTooltipContainer>
  );
}
